import React, { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export function Lightbox({ photos = [], index, onClose, onIndex }) {
  const open = index !== null && index >= 0;

  const prev = useCallback(() => onIndex((index - 1 + photos.length) % photos.length), [index, photos.length, onIndex]);
  const next = useCallback(() => onIndex((index + 1) % photos.length), [index, photos.length, onIndex]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, prev, next]);

  if (!open) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-matte/95 backdrop-blur-md"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
        onClick={onClose} data-testid="lightbox-overlay"
      >
        <button
          className="absolute top-6 right-6 text-soft-white/70 hover:text-gold transition-colors p-2"
          onClick={onClose} aria-label="Close" data-testid="lightbox-close"
        >
          <X size={30} />
        </button>

        {photos.length > 1 && (
          <button
            className="absolute left-3 sm:left-8 top-1/2 -translate-y-1/2 text-soft-white/80 hover:text-gold bg-matte/40 hover:bg-matte/60 rounded-full p-2 backdrop-blur-sm transition-colors"
            onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Previous photo" data-testid="lightbox-prev"
          >
            <ChevronLeft size={32} />
          </button>
        )}

        <motion.div
          key={index}
          className="relative max-w-[90vw] max-h-[85vh]"
          initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={photos[index]} alt={`Vernetta and Quin ${index + 1}`}
            className="max-w-[90vw] max-h-[85vh] w-auto h-auto object-contain rounded-lg ring-1 ring-gold/20 shadow-2xl"
            data-testid="lightbox-image"
          />
        </motion.div>

        {photos.length > 1 && (
          <>
            <button
              className="absolute right-3 sm:right-8 top-1/2 -translate-y-1/2 text-soft-white/80 hover:text-gold bg-matte/40 hover:bg-matte/60 rounded-full p-2 backdrop-blur-sm transition-colors"
              onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Next photo" data-testid="lightbox-next"
            >
              <ChevronRight size={32} />
            </button>
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 overline text-soft-white/60" data-testid="lightbox-counter">
              {index + 1} / {photos.length}
            </div>
          </>
        )}
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
